<script lang="ts" setup>
import type {Area} from "#magnolia-layer/types/magnolia-types";
import type {Delivery, Pages_V1} from "#magnolia-layer/types/api";
import type {Page} from "~/types/mgnl";
import type {AspectRatio} from "#base-layer/utils/parseAspectRatio";
import {
  useOffsetPagination,
  type UseOffsetPaginationReturn,
} from "@vueuse/core";
import type {UnwrapNestedRefs} from "vue";
import usePageQueryRef from "~/composables/usePageQueryRef";
import ErrorDisplayConcise from "~/components/ErrorDisplayConcise.vue";

const props = defineProps<{
  metadata: Area;
  pageRootLink: string;
}>();

const {t} = useI18n();

const pageRef = usePageQueryRef();
const limit = ref(8);
const offset = computed(() => {
  return (pageRef.value - 1) * limit.value;
});

const runtimeConfig = useRuntimeConfig();
const context = useMagnoliaContext();
const {data: content, error} = await useFetch<Delivery<Page>>(
  "/api/paginatedChildPages",
  {
    params: {
      deliveryApi: runtimeConfig.public.mgnl.apiPageOverview,
      offset: offset,
      limit: limit,
      path: props.pageRootLink,
      lang: context.searchParams?.lang,
    },
    watch: [offset],
  },
);

const offsetPagination = useOffsetPagination({
  page: pageRef,
  total: content.value?.total,
  pageSize: limit.value,
});
usePaginationHeadLinks(offsetPagination);
useErrorStatusForwarding(error);

const imageExtract = (image: any, aspectRatio: AspectRatio) => {
  const parsedRatio = parseAspectRatio(aspectRatio);
  return parsedRatio ? imageToExtractionArea(image, parsedRatio) : undefined;
};

const getPageLink = (path: string) => {
  if (path) {
    return {link: stripPrefix(path)};
  }
  return undefined;
};

const category = (pageCategory: string, authorName: string | undefined) => {
  if (pageCategory) {
    return pageCategory + "";
  }

  if (authorName) {
    return `${t("label.interviewBy")} ${authorName}`;
  }

  return undefined;
};
</script>

<template>
  <section class="col-span-full content-grid py-22">
    <h1 class="sr-only">{{ $t("label.overview") }}</h1>
    <ErrorDisplayConcise v-if="error" :error></ErrorDisplayConcise>
    <div v-else class="articles grid grid-cols-12 gap-3 items-end">
      <template v-for="(page, index) in content?.results" :key="index">
        <TextMediaLayout
          :compact="false"
          :border="false"
          :link="getPageLink(page['@path'])"
          :category="category(page.category, page.author?.authorName)"
          :title="page.title">
          <template #media>
            <MagnoliaPicture
              :asset="page.image"
              :modifiers="{
                extract: imageExtract(page.image, '1/1'),
                grayscale: 1,
              }"
              loading="lazy"
              class="max-h-80 h-full w-full *:w-full *:h-full *:object-contain *:object-center"
              sizes="400 md:600 lg:520 xl:640"
              v-if="page.image">
            </MagnoliaPicture>
          </template>
          <template v-if="page.abstract">{{ page.abstract }}</template>
        </TextMediaLayout>
      </template>
    </div>
  </section>
  <Pagination
    v-if="offsetPagination.pageCount.value > 1"
    :enable-pages="false"
    :enable-links="true"
    :pagination="offsetPagination"
    :total="content?.total ?? 0"></Pagination>
</template>

<style lang="scss" scoped></style>
